import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ListOrdered, Save } from "lucide-react";
import { api, formatApiErrorDetail, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminCategoryStrip() {
  const [items, setItems] = useState([]);
  const [savedOrder, setSavedOrder] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories/strip")
      .then(({ data }) => {
        setItems(data);
        setSavedOrder(data.map((item) => item.id).join(","));
      })
      .catch((error) => toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Unable to load category strip"))
      .finally(() => setLoading(false));
  }, []);

  const currentOrder = items.map((item) => item.id).join(",");
  const hasChanges = currentOrder !== savedOrder;

  const move = (index, offset) => {
    const target = index + offset;
    if (target < 0 || target >= items.length) return;
    setItems((current) => {
      const reordered = [...current];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/category-strip", { category_ids: items.map((item) => item.id) });
      setItems(data);
      setSavedOrder(data.map((item) => item.id).join(","));
      toast.success("Category strip order saved");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Unable to save category strip order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-category-strip-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <ListOrdered className="w-7 h-7 text-hyd-gold" /> Category Strip Management
          </h1>
          <p className="text-sm text-hyd-text2 mt-1">Reorder the categories shown in the storefront’s Shop by category strip.</p>
        </div>
        <Button
          type="button"
          data-testid="category-strip-save"
          onClick={save}
          disabled={!hasChanges || saving || loading}
          className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save order"}
        </Button>
      </div>

      <div className="mt-5 border border-hyd-border rounded-xl overflow-hidden bg-hyd-panel">
        {loading ? (
          <div className="p-8 text-center text-sm text-hyd-text2">Loading categories...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-hyd-text2">No active categories to show.</div>
        ) : (
          <ol className="divide-y divide-hyd-border">
            {items.map((item, index) => (
              <li key={item.id} data-testid={`category-strip-item-${item.slug}`} className="flex items-center gap-3 p-3 sm:px-4">
                <span className="w-7 text-right text-xs tabular-nums text-hyd-text3">{index + 1}</span>
                <div className="w-11 h-11 rounded-full overflow-hidden border border-hyd-border bg-hyd-panel2 shrink-0 grid place-items-center text-xs text-hyd-text3">
                  {item.image_url ? <img src={imageUrl(item.image_url)} alt="" className="w-full h-full object-cover" /> : item.name.slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-hyd-text truncate">{item.name}</div>
                  <div className="text-xs text-hyd-text3 truncate">
                    {item.parent_name || "Top-level category"} · {item.product_count} {item.product_count === 1 ? "product" : "products"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${item.name} up`}
                    data-testid={`category-strip-up-${item.slug}`}
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || saving}
                    className="text-hyd-text2 hover:text-hyd-gold"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${item.name} down`}
                    data-testid={`category-strip-down-${item.slug}`}
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1 || saving}
                    className="text-hyd-text2 hover:text-hyd-gold"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}